import { ArrowUpDown } from "lucide-react";

/**
 * Compact pill-style sort selector.
 * Usage:
 *   <SortSelect value={sort} onChange={setSort} options={[
 *     { value: "name_asc", label: "Name A → Z" },
 *     { value: "name_desc", label: "Name Z → A" },
 *   ]} testid="shop-sort" />
 */
export default function SortSelect({ value, onChange, options, testid, label = "Sort" }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs">
      <ArrowUpDown size={13} strokeWidth={1.5} className="text-muted-foreground" />
      <span className="uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-border rounded px-2 py-1 text-xs focus:outline-none focus:border-primary"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/**
 * Apply a sort key to a list. Returns a NEW sorted array.
 * Each comparator is total — never mutate input.
 */
export function applySort(list, sortKey, comparators) {
  const cmp = comparators[sortKey];
  if (!cmp) return list;
  return [...list].sort(cmp);
}

// Common comparator builders
export const cmpStr = (getter, dir = "asc") => (a, b) => {
  const av = (getter(a) || "").toString().toLowerCase();
  const bv = (getter(b) || "").toString().toLowerCase();
  if (av < bv) return dir === "asc" ? -1 : 1;
  if (av > bv) return dir === "asc" ? 1 : -1;
  return 0;
};

export const cmpNum = (getter, dir = "asc") => (a, b) => {
  const av = Number(getter(a)) || 0;
  const bv = Number(getter(b)) || 0;
  return dir === "asc" ? av - bv : bv - av;
};

export const cmpDate = (getter, dir = "desc") => (a, b) => {
  const av = new Date(getter(a) || 0).getTime();
  const bv = new Date(getter(b) || 0).getTime();
  return dir === "asc" ? av - bv : bv - av;
};

export const cmpBool = (getter, trueFirst = true) => (a, b) => {
  const av = !!getter(a);
  const bv = !!getter(b);
  if (av === bv) return 0;
  return trueFirst ? (av ? -1 : 1) : (av ? 1 : -1);
};
